import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import { useTranslation } from 'react-i18next';

import ClickableReviewsSummary, { type PublicReviewItem } from './ClickableReviewsSummary';
import type { ContentViewMode } from './PublicSpaceRoomList';
import TutorSchoolAffiliation from './tutor/TutorSchoolAffiliation';
import TutorSessionDiscoveryMeta from './tutor/TutorSessionDiscoveryMeta';
import type { TutorLessonOption, TutorSearchResult } from '../models/Tutor';
import { resolveLanguageLabel } from '../utils/languageOptions';
import { tutorAvatarInitials } from '../utils/tutorAvatarInitials';
import { tutorSkillLabel } from '../utils/tutorSkillI18n';

export const spaceTabToggleGroupSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 0,
    borderRadius: '0 !important',
    textTransform: 'none',
    fontWeight: 700,
    color: 'text.secondary',
    '&:not(:first-of-type)': {
      borderLeft: '1px solid',
      borderColor: 'divider',
      marginLeft: 0,
    },
    '&.Mui-selected': {
      color: 'text.primary',
      bgcolor: 'action.selected',
      '&:hover': {
        bgcolor: 'action.selected',
      },
    },
  },
} as const;

type ContentTab = 'tutors' | 'rooms';
export type LabelsPrefix = 'schoolPage' | 'orgPage';

function formatMoney(amount: number, currency: string | null | undefined, language: string): string {
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency || 'PLN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function cheapestLessonOption(tutor: TutorSearchResult): TutorLessonOption | null {
  return (tutor.lessonOptions ?? []).reduce<TutorLessonOption | null>(
    (best, option) => (best == null || option.price < best.price ? option : best),
    null,
  );
}

export interface PublicTutorsRoomPanelProps {
  labelsPrefix: LabelsPrefix;
  tutors: TutorSearchResult[];
  tutorError: string | null;
  roomCount: number;
  renderRooms: (viewMode: ContentViewMode) => React.ReactNode;
  tutorReviewsById: Record<string, PublicReviewItem[]>;
  tutorReviewsLoadingById: Record<string, boolean>;
  loadTutorReviews: (tutorId: string) => void;
  onOpenTutorProfile: (tutor: TutorSearchResult) => void;
  onOpenBooking: (tutor: TutorSearchResult) => void;
}

const PublicTutorsRoomPanel: React.FC<PublicTutorsRoomPanelProps> = ({
  labelsPrefix,
  tutors,
  tutorError,
  roomCount,
  renderRooms,
  tutorReviewsById,
  tutorReviewsLoadingById,
  loadTutorReviews,
  onOpenTutorProfile,
  onOpenBooking,
}) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<ContentTab>('tutors');
  const [contentViewMode, setContentViewMode] = useState<ContentViewMode>('cards');
  const uiLanguage = i18n.language || 'en';

  return (
    <Stack spacing={0.75}>
      <Box sx={{ px: { xs: 1.25, md: 1.5 } }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Box minWidth={0} flex={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', sm: '1.35rem' } }}
              >
                {activeTab === 'tutors'
                  ? t(`${labelsPrefix}.tutorsTitle`, 'Tutors')
                  : t(`${labelsPrefix}.roomsTitle`, 'Rooms')}
              </Typography>
              <Chip
                icon={
                  activeTab === 'tutors' ? <GroupsRoundedIcon /> : <MeetingRoomOutlinedIcon />
                }
                label={
                  activeTab === 'tutors'
                    ? t(`${labelsPrefix}.tutorCount`, '{{count}} tutors', { count: tutors.length })
                    : t(`${labelsPrefix}.roomCount`, '{{count}} rooms', { count: roomCount })
                }
                size="small"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {activeTab === 'tutors'
                ? t(`${labelsPrefix}.tutorsSubtitle`, 'Choose a tutor from this school and book a lesson.')
                : t(`${labelsPrefix}.roomsSubtitle`, 'Browse rehearsal rooms and book a session.')}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
          >
            <ToggleButtonGroup
              exclusive
              value={activeTab}
              onChange={(_event, value: ContentTab | null) => {
                if (value != null) setActiveTab(value);
              }}
              sx={{
                ...spaceTabToggleGroupSx,
                flex: { xs: 1, sm: '0 1 auto' },
                '& .MuiToggleButtonGroup-grouped': {
                  ...spaceTabToggleGroupSx['& .MuiToggleButtonGroup-grouped'],
                  px: { xs: 2.5, sm: 3 },
                  py: 1.25,
                  minWidth: { xs: 0, sm: 108 },
                  minHeight: 44,
                  flex: { xs: 1, sm: '0 1 auto' },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  lineHeight: 1.2,
                },
              }}
            >
              <ToggleButton value="tutors">{t(`${labelsPrefix}.tabTutorsShort`, 'Tutors')}</ToggleButton>
              <ToggleButton value="rooms">{t(`${labelsPrefix}.tabRoomsShort`, 'Rooms')}</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              value={contentViewMode}
              onChange={(_event, value: ContentViewMode | null) => {
                if (value != null) setContentViewMode(value);
              }}
              aria-label={t(`${labelsPrefix}.contentViewMode`, 'View mode')}
              sx={{
                ...spaceTabToggleGroupSx,
                '& .MuiToggleButtonGroup-grouped': {
                  ...spaceTabToggleGroupSx['& .MuiToggleButtonGroup-grouped'],
                  px: 1.25,
                  py: 0.85,
                  minWidth: 44,
                  minHeight: 44,
                },
              }}
            >
              <ToggleButton value="cards" aria-label={t(`${labelsPrefix}.viewCards`, 'Cards view')}>
                <GridViewOutlinedIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label={t(`${labelsPrefix}.viewList`, 'List view')}>
                <ViewListOutlinedIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Box>

      {activeTab === 'tutors' ? (
        <Paper
          id="school-tutors"
          elevation={2}
          sx={{
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <Stack spacing={1.5}>
            {tutorError ? <Alert severity="warning">{tutorError}</Alert> : null}

            {!tutorError && tutors.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6">{t(`${labelsPrefix}.noTutorsTitle`, 'No tutors yet')}</Typography>
                <Typography color="text.secondary">
                  {t(`${labelsPrefix}.noTutorsBody`, 'This school does not have public tutors available right now.')}
                </Typography>
              </Paper>
            ) : null}

            <Grid container spacing={1.5}>
              {tutors.map((tutor) => {
                const cheapest = cheapestLessonOption(tutor);
                const reviewCount = tutor.reviewCount ?? 0;
                const isListView = contentViewMode === 'list';
                return (
                  <Grid item xs={12} md={isListView ? 12 : 6} key={tutor.tutorId} sx={{ display: 'flex' }}>
                    <Card
                      variant="outlined"
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenTutorProfile(tutor)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onOpenTutorProfile(tutor);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: (theme) => theme.shadows[3],
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={{ xs: 1.5, sm: isListView ? 2 : 3 }}
                        alignItems="stretch"
                        sx={{ flex: 1, height: '100%' }}
                      >
                        <Stack
                          spacing={0.75}
                          alignItems="center"
                          sx={{
                            minWidth: isListView ? { xs: 96, sm: 112 } : { xs: 88, sm: 112 },
                            flexShrink: 0,
                          }}
                        >
                          <Avatar
                            src={tutor.profileImageUrl || undefined}
                            alt=""
                            variant="rounded"
                            sx={{
                              width: isListView ? { xs: 96, sm: 112 } : { xs: 88, sm: 112 },
                              height: isListView ? { xs: 96, sm: 112 } : { xs: 88, sm: 112 },
                              borderRadius: 2,
                              bgcolor: '#4AA7C6',
                              color: '#ffffff',
                              fontSize: isListView ? { xs: '2rem', sm: '2.25rem' } : { xs: '2rem', sm: '2.5rem' },
                            }}
                          >
                            {tutorAvatarInitials(tutor.name)}
                          </Avatar>
                          {!isListView ? (
                            <Stack spacing={0.25} sx={{ width: '100%' }}>
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                <EventAvailableRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {t('tutorSearch.statsConfirmedBookings', 'Bookings: {{count}}', {
                                    count: tutor.bookingCount ?? 0,
                                  })}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                <GroupsRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {t('tutorSearch.statsStudents', 'Students: {{count}}', {
                                    count: tutor.studentCount ?? 0,
                                  })}
                                </Typography>
                              </Stack>
                            </Stack>
                          ) : null}
                        </Stack>

                        <Stack spacing={1.25} flex={1} minWidth={0} sx={{ height: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                            <Box minWidth={0}>
                              <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                                {tutor.name}
                              </Typography>
                              <Box onClick={(event) => event.stopPropagation()}>
                                <TutorSchoolAffiliation
                                  organizationType={tutor.organizationType}
                                  spaceId={tutor.spaceId}
                                  spaceName={tutor.spaceName}
                                  variant="subtitle2"
                                />
                              </Box>
                              {(tutor.headline ?? '').trim() ? (
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  sx={{ lineHeight: 1.35, mt: 0.75, mb: 0.25 }}
                                >
                                  {(tutor.headline ?? '').trim()}
                                </Typography>
                              ) : null}
                              {isListView ? (
                                <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                                  <Stack direction="row" spacing={0.4} alignItems="center">
                                    <EventAvailableRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {t('tutorSearch.statsConfirmedBookings', 'Bookings: {{count}}', {
                                        count: tutor.bookingCount ?? 0,
                                      })}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={0.4} alignItems="center">
                                    <GroupsRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {t('tutorSearch.statsStudents', 'Students: {{count}}', {
                                        count: tutor.studentCount ?? 0,
                                      })}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              ) : null}
                            </Box>
                            {cheapest ? (
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={t('tutorSearch.fromLessonPrice', 'From {{price}} / lesson', {
                                  price: formatMoney(cheapest.price, cheapest.lessonPriceCurrency, uiLanguage),
                                })}
                                sx={{ fontWeight: 700, flexShrink: 0 }}
                              />
                            ) : null}
                          </Stack>

                          <Box onClick={(event) => event.stopPropagation()}>
                            <ClickableReviewsSummary
                              averageRating={tutor.avgReviewRating ?? 0}
                              reviewCount={reviewCount}
                              reviews={tutorReviewsById[tutor.tutorId] ?? []}
                              loading={Boolean(tutorReviewsLoadingById[tutor.tutorId])}
                              onOpen={() => loadTutorReviews(tutor.tutorId)}
                              ratingName={`school-tutor-rating-${tutor.tutorId}`}
                              variant="compact"
                              stopPropagation
                            />
                          </Box>

                          <TutorSessionDiscoveryMeta supportedSessionTypes={tutor.supportedSessionTypes} />

                          {tutor.skills?.length ? (
                            <Box>
                              <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
                                <MusicNoteRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                <Typography variant="overline" color="text.secondary">
                                  {t('tutorSearch.skills', 'Skills')}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                {tutor.skills.slice(0, isListView ? 6 : undefined).map((skill) => (
                                  <Chip
                                    key={`${tutor.tutorId}-skill-${skill}`}
                                    size="small"
                                    label={tutorSkillLabel(t, skill)}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          ) : null}

                          {tutor.languages?.length ? (
                            <Box>
                              <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
                                <TranslateRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                <Typography variant="overline" color="text.secondary">
                                  {t('tutorSearch.languages', 'Languages')}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                {tutor.languages.map((code) => (
                                  <Chip
                                    key={`${tutor.tutorId}-lang-${code}`}
                                    size="small"
                                    label={resolveLanguageLabel(code, uiLanguage)}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          ) : null}

                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{ mt: 'auto', pt: 1.5, width: '100%' }}
                          >
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenBooking(tutor);
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              {t(`${labelsPrefix}.bookLesson`, 'Book lesson')}
                            </Button>
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenTutorProfile(tutor);
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              {t(`${labelsPrefix}.viewProfile`, 'View profile')}
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Paper>
      ) : (
        <Paper
          id="school-rooms"
          elevation={2}
          sx={{
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          {renderRooms(contentViewMode)}
        </Paper>
      )}
    </Stack>
  );
};

export default PublicTutorsRoomPanel;
